import { dynamicTranslations, generateActivityTitleTranslation } from "@shared/utils/dynamicTranslation";
import { useNavigation } from "@react-navigation/native";
import { usePost } from "app/queryProvider";
import { ActivityType, IPathwayActivity } from "app/_modules/pathways/entities";
import { extractMeasurementValues, formatMeasurementValue, IMeasurementData } from "app/_modules/core/utils";
import { getQuestionnaireDisplayState } from "app/_modules/core/questionnaire";
import type { TTourAnchor } from "app/_modules/onboarding";
import { ActivityName } from "../../../types";
import { ACTIVITY_CARD_STATUS } from "../../../constants";
import { ActivityCtaButton } from "./ActivityCtaButton";
import {
    TActivityBucket,
    mapActivityToCardStatus,
    mapCardStatusToReadingTone,
    getMedicationBadgeStatus,
    getActionLabelKey,
    getSupportLine,
    getUnlockLabel,
    hasRepeatRequest,
    isNearDeadline,
} from "../../../utils/activityCard";
import { MedicationBadge } from "./MedicationBadge";
import { StepCard } from "./StepCard";
import { ActivityActionCard } from "./ActivityActionCard";
import { ActivityLaterCard } from "./ActivityLaterCard";
import { ActivityReadingRow } from "./ActivityReadingRow";

interface IActivityCardProps {
    activity: IPathwayActivity;
    bucket: TActivityBucket;
    onPress: () => void;
    disabled?: boolean;
    ctaAnchor?: TTourAnchor;
    ordinal?: number;
}

const getActivityDisplayValue = (
    activity: IPathwayActivity,
    t: (key: any, options?: any) => string,
): { formattedValue?: string; showViewAnswersButton: boolean } => {
    let displayValue: string | number = '';
    let displayUnit = '';
    let shouldShowValue = false;
    let showViewAnswersButton = false;

    if (activity.activityType === ActivityType.PATHWAY_VITAL_PARAMETER && activity.measurement) {
        const measurement = activity.measurement as any;
        const measurementData: IMeasurementData = {
            type: activity.activityName,
            measurement,
            timestamp: String(measurement.measuredAt || activity.completedAt || ''),
        };
        const extractedValues = extractMeasurementValues(measurementData);
        const formatted = formatMeasurementValue(activity.activityName, extractedValues, t);
        displayValue = formatted.value;
        displayUnit = formatted.showUnit ? formatted.unit : '';
        shouldShowValue = formatted.value !== '--' && formatted.value !== '';
    }

    if (activity.activityType === ActivityType.PATHWAY_QUESTIONNAIRE && activity.questionnaireResult) {
        const state = getQuestionnaireDisplayState(activity.questionnaireResult);
        displayValue = state.displayValue;
        shouldShowValue = state.shouldShowValue;
        showViewAnswersButton = state.showViewAnswersButton;
    }

    return {
        formattedValue: shouldShowValue ? `${displayValue}${displayUnit ? ` ${displayUnit}` : ''}` : undefined,
        showViewAnswersButton,
    };
};

const ViewAnswersButton = ({
    onPress,
    loading,
    t,
}: {
    onPress: () => void;
    loading: boolean;
    t: (key: any, options?: any) => string;
}) => (
    <ActivityCtaButton icon="eye" variant="outline" onPress={onPress} disabled={loading} compact>
        {loading ? t('telemonitoring.report.downloading') : t('screens.activities.questionnaire.viewAnswers')}
    </ActivityCtaButton>
);

export const ActivityCard = ({ activity, bucket, onPress, disabled = false, ctaAnchor, ordinal }: IActivityCardProps) => {
    const { t } = dynamicTranslations();
    const navigation = useNavigation<any>();
    const { mutate: generatePdf, isPending: isGeneratingPdf } = usePost<Record<string, never>, { pdfUrl: string }>({
        options: {
            onSuccess: ({ pdfUrl }) => {
                navigation.navigate('PdfDocumentPage', { pdfUrl });
            },
        },
    });

    if (activity.activityName === ActivityName.STEP) {
        return <StepCard activity={activity} allowPress={!disabled} />;
    }

    const cardStatus = mapActivityToCardStatus(activity, bucket);
    const activityTitle = t(generateActivityTitleTranslation(activity.activityName, activity.activityType));
    const commonProps = {
        activityType: activity.activityType,
        activityName: activity.activityName,
        title: ordinal
            ? t('screens.activities.card.measurementOrdinal', { title: activityTitle, n: ordinal })
            : activityTitle,
    };
    const supportLine = getSupportLine(activity, bucket, cardStatus);
    const supportText = supportLine.key ? t(supportLine.key, supportLine.params) : undefined;
    const isDrug = activity.activityType === ActivityType.PATHWAY_DRUG;

    const handleViewAnswers = () => {
        if (!activity.questionnaireId) return;
        generatePdf({
            resource: { name: 'questionnaire-results', id: activity.questionnaireResultId },
            url: '/generate-pdf',
            data: {},
        });
    };

    const { formattedValue, showViewAnswersButton } = getActivityDisplayValue(activity, t);

    if (hasRepeatRequest(activity) && !disabled) {
        return (
            <ActivityActionCard
                {...commonProps}
                title={t(`vitalParameters.${activity.activityName}.repeatTitle`)}
                variant="warning"
                blockAction
                value={formattedValue}
                whenText={supportText}
                showClockIcon
                actionLabel={t('screens.activities.card.repeat.action')}
                onPress={() => navigation.navigate('WhoMeasured', {
                    measurementId: activity.pendingAlert!.measId,
                    vitalParameterName: activity.pendingAlert!.vitPName,
                })}
            />
        );
    }

    if (cardStatus === ACTIVITY_CARD_STATUS.TODO) {
        const nearDeadline = isNearDeadline(activity, bucket, cardStatus);
        return (
            <ActivityActionCard
                {...commonProps}
                variant={nearDeadline ? 'warning' : 'default'}
                whenText={supportText}
                urgentNote={nearDeadline ? t('screens.activities.card.supportLine.nearDeadlineNote') : undefined}
                actionLabel={t(getActionLabelKey(activity.activityType))}
                onPress={onPress}
                disabled={disabled}
                ctaAnchor={ctaAnchor}
            />
        );
    }

    if (cardStatus === ACTIVITY_CARD_STATUS.LOCKED) {
        const unlockLabel = getUnlockLabel(activity, bucket);
        return (
            <ActivityLaterCard
                {...commonProps}
                whenText={supportText}
                unlockLabel={t(unlockLabel.key, unlockLabel.params)}
            />
        );
    }

    const tone = mapCardStatusToReadingTone(cardStatus);

    const medicationBadgeStatus = isDrug ? getMedicationBadgeStatus(activity) : null;

    const trailing = medicationBadgeStatus ? (
        <MedicationBadge status={medicationBadgeStatus} />
    ) : showViewAnswersButton ? (
        <ViewAnswersButton onPress={handleViewAnswers} loading={isGeneratingPdf} t={t} />
    ) : undefined;

    return (
        <ActivityReadingRow
            {...commonProps}
            timeText={supportText}
            tone={tone}
            value={trailing ? undefined : formattedValue}
            trailing={trailing}
        />
    );
};

export default ActivityCard;
